import { isEmpty } from '@ember/utils';
import { PlanFeatures, PlanLimits } from 'cio/models/account';
import * as Sentry from '@sentry/ember';
import { PlanType } from 'cio/models/plan';
import { ProductApp } from 'cio/utils/product-app-info';

export type PlanName =
  | 'Essentials'
  | 'Premium'
  | 'Enterprise'
  | 'CdpStarter'
  | 'CdpPremium'
  | 'StartupEssentials'
  | 'StartupBasic'
  | 'StartupPlus';
type HighlightRoute = { label: string; route: string; query?: Record<string, string> };
type HighlightLink = { label: string; href: string };
type Highlight = string | HighlightLink | HighlightRoute;

export type Plan = {
  name: string;
  basePrice: number;
  baseCustomerCount: number;
  baseObjectCount: number;
  baseMeteredRate: number;
  baseMeteredObjectRate: number;
  highlights: Highlight[];
  description: string;
  planLimits: Partial<Record<PlanLimits, { value: number }>>;
  planFeatures: PlanFeatures[];
  planType: PlanType;
  baseApiCalls?: number;
  slug?: string;
};

export const Plans: Record<PlanName, Plan> = {
  Essentials: {
    name: 'Essentials',
    basePrice: 10000,
    baseCustomerCount: 5000,
    baseObjectCount: 500,
    baseMeteredRate: 0.009,
    baseMeteredObjectRate: 0.009,
    highlights: [
      `<strong>5000</strong> profiles`,
      `<strong>1 million</strong> emails per month`,
      `<strong>500</strong> object profiles & 2 object types`,
    ],
    description: 'For small companies who are gaining momentum.',
    planLimits: { [PlanLimits.workspaces]: { value: 2 }, [PlanLimits.objectTypes]: { value: 2 } },
    planFeatures: [PlanFeatures.ObjectTypes],
    planType: PlanType.Essentials,
    baseApiCalls: 1000000,
    slug: '202301_pricing_essentials',
  },
  Premium: {
    name: 'Premium',
    basePrice: 100000,
    baseCustomerCount: 50000,
    baseObjectCount: 5000,
    baseMeteredRate: 0.007,
    baseMeteredObjectRate: 0.009,
    highlights: [
      `<strong>Custom</strong> profile volume`,
      `<strong>Custom</strong> email volume`,
      `<strong>5000</strong> object profiles & 10 object types`,
    ],
    description: 'For end-to-end marketing for high-growth companies.',
    planLimits: { [PlanLimits.workspaces]: { value: 0 }, [PlanLimits.objectTypes]: { value: 10 } },
    planFeatures: [PlanFeatures.WarehouseSync, PlanFeatures.Collections, PlanFeatures.ObjectTypes],
    planType: PlanType.Premium,
    baseApiCalls: 1000000,
  },
  Enterprise: {
    name: 'Enterprise',
    basePrice: 0,
    baseCustomerCount: 0,
    baseObjectCount: 5000,
    baseMeteredRate: 0,
    baseMeteredObjectRate: 0.009,
    highlights: [
      `<strong>Custom</strong> profile volume`,
      `<strong>Custom</strong> email volume`,
      `<strong>5000</strong> object profiles & 10 object types`,
    ],
    description: 'For larger companies scaling lifecycle marketing.',
    planLimits: { [PlanLimits.workspaces]: { value: 0 }, [PlanLimits.objectTypes]: { value: 10 } },
    planFeatures: [PlanFeatures.WarehouseSync, PlanFeatures.Collections, PlanFeatures.ObjectTypes],
    planType: PlanType.Enterprise,
    baseApiCalls: 1000000,
  },
  CdpStarter: {
    name: 'Starter',
    basePrice: 0,
    baseCustomerCount: 0,
    baseObjectCount: 0,
    baseMeteredRate: 0,
    baseMeteredObjectRate: 0,
    highlights: [
      `<strong>1 million</strong> monthly API calls`,
      `<strong>Unlimited</strong> sources`,
      `<strong>Unlimited</strong> destinations`,
      `<strong>2</strong> workspaces`,
    ],
    description: 'Easily centralize customer data',
    planLimits: { [PlanLimits.workspaces]: { value: 2 } },
    planFeatures: [],
    planType: PlanType.CdpStarter,
    baseApiCalls: 1000000,
    slug: 'cdp_starter_202304',
  },
  CdpPremium: {
    name: 'Premium',
    basePrice: 0,
    baseCustomerCount: 0,
    baseObjectCount: 0,
    baseMeteredRate: 0,
    baseMeteredObjectRate: 0,
    highlights: [
      `<strong>10 million+</strong> monthly API calls`,
      `<strong>Unlimited</strong> sources`,
      `<strong>Unlimited</strong> destinations`,
      `<strong>Unlimited</strong> workspaces`,
    ],
    description: 'A fully-featured CDP that scales',
    planLimits: { [PlanLimits.workspaces]: { value: 0 } },
    planFeatures: [],
    planType: PlanType.CdpPremium,
    baseApiCalls: 1000000,
  },
  StartupEssentials: {
    name: 'Startup Essentials',
    basePrice: 10000,
    baseCustomerCount: 5000,
    baseObjectCount: 500,
    baseMeteredRate: 0.009,
    baseMeteredObjectRate: 0.009,
    highlights: [
      '5000 contacts & 500 objects',
      'Send Emails, Push, In-App, and SMS',
      'Collections',
      'Customer.io Community Support',
      '$1,200 value',
    ],
    description: 'Startup essentials includes everything you need to start sending messages to your customers',
    planLimits: { [PlanLimits.workspaces]: { value: 2 }, [PlanLimits.objectTypes]: { value: 2 } },
    planFeatures: [PlanFeatures.ObjectTypes],
    planType: PlanType.Essentials,
    baseApiCalls: 1000000,
    // eslint-disable-next-line spellcheck/spell-checker
    slug: 'plan_NTh5DaIQSCHFBH',
  },
  StartupBasic: {
    name: 'Startup Basic',
    basePrice: 15000,
    baseCustomerCount: 12000,
    baseObjectCount: 500,
    baseMeteredRate: 0.005,
    baseMeteredObjectRate: 0.009,
    highlights: [
      '12,000 contacts & 500 objects',
      'Send Emails, Push, In-App, and SMS',
      'Collections',
      'Customer.io Community Support',
      '$1,800 value',
    ],
    description: 'Startup basic includes everything you need to start sending messages to your customers',
    planLimits: { [PlanLimits.workspaces]: { value: 0 }, [PlanLimits.objectTypes]: { value: 2 } },
    planFeatures: [PlanFeatures.WarehouseSync, PlanFeatures.Collections, PlanFeatures.ObjectTypes],
    planType: PlanType.Startup,
    baseApiCalls: 1000000,
    slug: '202303_startup_basic_static',
  },
  StartupPlus: {
    name: 'Startup +Plus',
    basePrice: 100000,
    baseCustomerCount: 100000,
    baseObjectCount: 5000,
    baseMeteredRate: 0.004,
    baseMeteredObjectRate: 0.009,
    highlights: [
      'Includes 100,000 contacts',
      '5,000 objects & 10 object types',
      'Collections',
      'Limited technical support',
      '1-hour onboarding and planning call',
      '$12,000 value',
    ],
    description: 'Startup +Plus includes all basic features plus additional technical and onboarding support',
    planLimits: { [PlanLimits.workspaces]: { value: 0 }, [PlanLimits.objectTypes]: { value: 10 } },
    planFeatures: [PlanFeatures.WarehouseSync, PlanFeatures.Collections, PlanFeatures.ObjectTypes],
    planType: PlanType.Startup,
    baseApiCalls: 1000000,
    // eslint-disable-next-line spellcheck/spell-checker
    slug: 'plan_H4CyyclaRROnke',
  },
};

export const currentOfferingsByPlanType = [
  PlanType.Startup,
  PlanType.Essentials,
  PlanType.Premium,
  PlanType.Enterprise,
  PlanType.CdpStarter,
  PlanType.CdpPremium,
];

export const DataPipelinesPlanTypes = [Plans.CdpPremium.planType, Plans.CdpStarter.planType];

const JourneysUpgradePlan = {
  [Plans.Essentials.planType]: Plans.Premium.planType,
  [Plans.Premium.planType]: Plans.Enterprise.planType,
  [Plans.Enterprise.planType]: Plans.Enterprise.planType,
  [Plans.CdpStarter.planType]: Plans.Essentials.planType,
  [Plans.CdpPremium.planType]: Plans.Essentials.planType,
};

const DatapipelinesUpgradePlan = {
  [Plans.Essentials.planType]: Plans.CdpStarter.planType,
  [Plans.Premium.planType]: Plans.CdpStarter.planType,
  [Plans.Enterprise.planType]: Plans.CdpStarter.planType,
  [Plans.CdpStarter.planType]: Plans.CdpPremium.planType,
  [Plans.CdpPremium.planType]: Plans.CdpPremium.planType,
};

export function getUpgradePlanTypes(
  isPremiumTrial: boolean,
  isJourneysTrial: boolean,
  currentPlanType: PlanType | undefined,
) {
  let journeysUpgradePlan = currentPlanType != null ? JourneysUpgradePlan[currentPlanType] : Plans.Essentials.planType;
  let datapipelinesUpgradePlan =
    currentPlanType != null ? DatapipelinesUpgradePlan[currentPlanType] : Plans.CdpStarter.planType;
  if (isPremiumTrial) {
    journeysUpgradePlan = Plans.Premium.planType;
  } else if (isJourneysTrial) {
    journeysUpgradePlan = Plans.Essentials.planType;
  }
  return {
    [ProductApp.JOURNEYS]: journeysUpgradePlan,
    [ProductApp.DATA_PIPELINES]: datapipelinesUpgradePlan,
  };
}

function fallback(): Plan {
  return Plans.Essentials;
}

export default function getPlanType(type: string): Plan {
  let planType = Plans[type as PlanName];
  if (isEmpty(planType) || planType === null || planType === undefined) {
    Sentry.captureException(`Plan type ${type} not found, falling back to ${fallback()}`);
    return fallback();
  }
  return planType;
}
